import { CustomerAddressType } from '@/core/collections/contracts-api/customers/customer-address-types/customer-address-type.interface';

type StreetParts = {
   street?: string | null;
   house_number?: string | null;
   box?: string | null;
};

type CityParts = {
   city?: {
      postal_code?: string | null;
      name?: string | Record<string, string> | null;
      country?: {
         name?: string | Record<string, string> | null;
      } | null;
   } | null;
};

export function getLocalizedCityName(city: CityParts['city'], locale: string): string {
   const name = city?.name;
   if (name == null || name === '') return '';
   if (typeof name === 'string') return name.trim();
   return (name[locale] || Object.values(name).find(Boolean) || '').trim();
}

type CountryNameParts =
   | {
        name?: string | Record<string, string> | null;
     }
   | null
   | undefined;

export function getLocalizedCountryName(country: CountryNameParts, locale: string): string {
   const name = country?.name;
   if (name == null || name === '') return '';
   if (typeof name === 'string') return name.trim();
   return (name[locale] || Object.values(name).find(Boolean) || '').trim();
}

export function getCustomerAddressTypeDisplayName(type: CustomerAddressType | undefined, locale: string): string {
   const name = type?.name;
   if (name == null || name === '') return '';
   if (typeof name === 'string') return name.trim();
   return (name[locale] || Object.values(name).find(Boolean) || '').trim();
}

/** Street, house number, and optional box (single space‑separated line). */
export function formatCustomerAddressStreetHouseBox(address: StreetParts): string {
   const main = [address.street, address.house_number].filter(Boolean).join(' ').trim();
   const box = address.box?.trim();
   if (main && box) return `${main} ${box}`.trim();
   return main || box || '';
}

/** Postal code and localized municipality name. */
export function formatCustomerAddressPostalCity(address: CityParts, locale: string, options?: { includeCountry?: boolean }): string {
   const postal = address.city?.postal_code?.trim() || '';
   const cityName = getLocalizedCityName(address.city, locale);
   const citySegment = [postal, cityName].filter(Boolean).join(' ').trim();

   if (!options?.includeCountry) {
      return citySegment;
   }

   const countryName = getLocalizedCountryName(address.city?.country, locale);
   if (!citySegment) return countryName;
   if (!countryName) return citySegment;
   return `${citySegment},  ${countryName}`;
}

/** One line for compact dropdown labels (no address type). */
export function formatCustomerAddressSingleLine(address: StreetParts & CityParts, locale: string): string {
   const streetLine = formatCustomerAddressStreetHouseBox(address);
   const cityLine = formatCustomerAddressPostalCity(address, locale);
   return [streetLine, cityLine].filter(Boolean).join(', ');
}
